// El guardado automático de un proyecto del Estudio de Video —
// ADR-2026-11-02-el-estudio-guarda-solo-lo-ultimo-y-avisa-si-falla.
//
// `guardar` es el `saveProject` del editor: un callback que cambia de identidad cada
// vez que cambia algo que se guarda. Ese cambio de identidad ES la señal de "hay algo
// nuevo": se arma un guardado para dentro de 3 s y, si llega otro cambio antes, se
// corre.
//
// Lo que este hook arregla del mecanismo anterior:
//   • "Guardar al salir" corría en el cleanup de un efecto que dependía de `guardar`, o
//     sea en CADA cambio y con la closure del render anterior: un UPDATE por cada
//     pointermove de un arrastre, y al abrir el proyecto uno con `clips: []` (la closure
//     de antes de cargar, con la marca de "cargado" ya prendida). Ahora el guardado
//     siempre lee el `guardar` más reciente por ref y el de salida corre una sola vez.
//   • Un guardado que fallaba no avisaba ni reintentaba. Ahora devuelve `fallo` (para
//     el aviso) y reintenta solo, con esperas crecientes.
//   • Cerrar o recargar la pestaña perdía lo de los últimos 3 s: al ocultarse la página
//     se guarda lo pendiente en el momento.
//   • Dos guardados podían viajar a la vez y llegar en desorden: ahora van en fila.
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { ESPERA_DEL_GUARDADO_MS, esperaAntesDeReintentar } from './guardadoDelProyecto';

/** `alCerrar`: la página se está yendo; el guardado tiene que sobrevivir a la descarga. */
export type OpcionesDeGuardado = { alCerrar?: boolean };

export function useGuardadoDelProyecto(
  /** Guarda el estado actual. `true` si quedó guardado (o no había nada que guardar). */
  guardar: (opciones?: OpcionesDeGuardado) => Promise<boolean>,
  /** Prendida recién cuando el proyecto terminó de cargar: antes, guardar pisaría lo real. */
  puedeGuardarRef: MutableRefObject<boolean>,
): { fallo: boolean; guardarAhora: (opciones?: OpcionesDeGuardado) => Promise<void> } {
  const [fallo, setFallo] = useState(false);
  const guardarRef = useRef(guardar);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendienteRef = useRef(false);
  const fallidosRef = useRef(0);
  const filaRef = useRef<Promise<void>>(Promise.resolve());
  const desmontadoRef = useRef(false);

  // Un guardado común que todavía no volvió (ver la salida de la página, abajo).
  const enVueloRef = useRef(false);
  const limpiarTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const guardarAhora = useCallback((opciones?: OpcionesDeGuardado): Promise<void> => {
    limpiarTimer();
    // La página se está yendo: sin fila y en el acto. Encolado detrás de otro guardado
    // en vuelo, el pedido saldría cuando la página ya no existe. Tampoco hay reintento:
    // es el último intento.
    if (opciones?.alCerrar) {
      pendienteRef.current = false;
      // Si la página sigue viva y no se guardó, vuelve a quedar pendiente (lo toma el
      // próximo guardado común o la próxima salida).
      void guardarRef.current(opciones)
        .then((ok) => {
          if (!ok) pendienteRef.current = true;
        })
        .catch(() => {
          pendienteRef.current = true;
        });
      return Promise.resolve();
    }
    const correr = async () => {
      // Se baja ANTES de guardar: si algo cambia mientras viaja, el efecto de abajo la
      // vuelve a subir y arma otro guardado.
      pendienteRef.current = false;
      let ok = false;
      enVueloRef.current = true;
      try {
        ok = await guardarRef.current(opciones);
      } catch {
        ok = false;
      } finally {
        enVueloRef.current = false;
      }
      if (ok) {
        fallidosRef.current = 0;
        setFallo(false);
        return;
      }
      pendienteRef.current = true;
      fallidosRef.current += 1;
      setFallo(true);
      // Con el editor cerrado no se reintenta: ya no hay a quién avisarle, y el
      // guardado de salida fue el último intento.
      if (!desmontadoRef.current && !timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          void guardarAhora();
        }, esperaAntesDeReintentar(fallidosRef.current));
      }
    };
    const vuelta = filaRef.current.then(correr, correr);
    filaRef.current = vuelta.catch(() => {});
    return vuelta;
  }, []);

  // El `guardar` más reciente, siempre. Va antes que el efecto que arma el guardado
  // para que ése ya lea el nuevo.
  useEffect(() => {
    guardarRef.current = guardar;
  }, [guardar]);

  // Cambió algo que se guarda → guardado dentro de 3 s (un cambio nuevo corre la espera).
  useEffect(() => {
    if (!puedeGuardarRef.current) return;
    pendienteRef.current = true;
    limpiarTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void guardarAhora();
    }, ESPERA_DEL_GUARDADO_MS);
  }, [guardar, guardarAhora, puedeGuardarRef]);

  // Salir del editor (navegar a otra pantalla u otro proyecto): lo pendiente se guarda
  // una sola vez, con lo último. En StrictMode el primer cleanup simulado no guarda
  // nada porque el proyecto todavía no cargó, y el montaje de nuevo baja la marca.
  useEffect(() => {
    desmontadoRef.current = false;
    return () => {
      desmontadoRef.current = true;
      limpiarTimer();
      if (puedeGuardarRef.current && pendienteRef.current) void guardarAhora();
    };
  }, [guardarAhora, puedeGuardarRef]);

  // Cerrar o recargar la pestaña no desmonta nada: se guarda al ocultarse la página,
  // con `alCerrar` para que el pedido sobreviva a la descarga (`keepalive`, ver
  // `guardarAlCerrar.ts`): un pedido común el navegador lo cancela al irse la página.
  // ADR-2026-11-03-el-ultimo-guardado-sobrevive-al-cierre.
  useEffect(() => {
    // También con un guardado común EN VUELO: si la página se va, el navegador lo
    // cancela y lo último se perdía (review Greptile #242). Si el común llega primero,
    // el de salida no pisa nada (va con la versión de antes y no encuentra la fila).
    const hayQueGuardar = () =>
      puedeGuardarRef.current && (pendienteRef.current || enVueloRef.current);
    const alOcultar = () => {
      if (document.visibilityState !== 'hidden') return;
      if (hayQueGuardar()) void guardarAhora({ alCerrar: true });
    };
    const alSalirDeLaPagina = () => {
      if (hayQueGuardar()) void guardarAhora({ alCerrar: true });
    };
    document.addEventListener('visibilitychange', alOcultar);
    window.addEventListener('pagehide', alSalirDeLaPagina);
    return () => {
      document.removeEventListener('visibilitychange', alOcultar);
      window.removeEventListener('pagehide', alSalirDeLaPagina);
    };
  }, [guardarAhora, puedeGuardarRef]);

  return { fallo, guardarAhora };
}
